/**
 * Services Lab - Shopify Theme JavaScript
 * Animations and interactions for Services Lab sections
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const CONFIG = {
    observerThreshold: 0.15,
    counterDuration: 2000,
    scrollSmoothness: 0.1,
    parallaxFactor: 0.5
  };

  // ============================================
  // Intersection Observer for Reveal Animations
  // ============================================
  function initRevealAnimations() {
    const revealElements = document.querySelectorAll('.sl-reveal, .sl-stagger');
    
    if (!revealElements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('sl-visible');
          // Optionally unobserve after animation
          // observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: CONFIG.observerThreshold,
      rootMargin: '0px 0px -50px 0px'
    });

    revealElements.forEach(el => observer.observe(el));
  }

  // ============================================
  // Counter Animation
  // ============================================
  function animateCounter(element) {
    const target = parseInt(element.dataset.target, 10);
    const suffix = element.dataset.suffix || '';
    const duration = parseInt(element.dataset.duration, 10) || CONFIG.counterDuration;
    
    if (isNaN(target)) return;

    const start = 0;
    const startTime = performance.now();
    
    function easeOutQuart(t) {
      return 1 - Math.pow(1 - t, 4);
    }

    function updateCounter(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuart(progress);
      const currentValue = Math.floor(start + (target - start) * easedProgress);
      
      element.textContent = currentValue;
      
      // Add suffix span if exists
      if (suffix && progress === 1) {
        element.innerHTML = `${target}<span class="sl-counter__suffix">${suffix}</span>`;
      }
      
      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      }
    }

    requestAnimationFrame(updateCounter);
  }

  function initCounterAnimations() {
    const counters = document.querySelectorAll('.sl-counter__value[data-target]');
    
    if (!counters.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('sl-animated');
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.5
    });

    counters.forEach(counter => {
      // Set initial state
      counter.textContent = '0';
      observer.observe(counter);
    });
  }

  // ============================================
  // Scroll-Driven Animations (Hero & CTA)
  // ============================================
  function initScrollAnimations() {
    const heroSections = document.querySelectorAll('.sl-hero--animated');
    const ctaSections = document.querySelectorAll('.sl-cta--animated');
    
    if (!heroSections.length && !ctaSections.length) return;

    // Check for native scroll-timeline support
    const supportsScrollTimeline = CSS.supports('animation-timeline', 'scroll()');
    
    if (!supportsScrollTimeline) {
      // Fallback: Use scroll event with requestAnimationFrame
      let ticking = false;

      function handleScroll() {
        if (!ticking) {
          requestAnimationFrame(() => {
            updateScrollAnimations();
            ticking = false;
          });
          ticking = true;
        }
      }

      function updateScrollAnimations() {
        const scrollY = window.scrollY;
        const windowHeight = window.innerHeight;

        // Hero animations
        heroSections.forEach(hero => {
          const rect = hero.getBoundingClientRect();
          const heroTop = rect.top + scrollY;
          const heroHeight = rect.height;
          
          // Calculate progress (0 at top of section, 1 when scrolled past)
          const scrollProgress = Math.max(0, Math.min(1, 
            (scrollY - heroTop + windowHeight) / (heroHeight + windowHeight)
          ));

          // Scale: starts at 1.5, ends at 1 (150% -> 100%)
          const startScale = parseFloat(hero.dataset.scaleStart) || 1.5;
          const endScale = parseFloat(hero.dataset.scaleEnd) || 1;
          const scale = startScale - (startScale - endScale) * scrollProgress;

          // Rotation: starts at 10deg, ends at 0deg
          const startRotate = parseFloat(hero.dataset.rotateStart) || 10;
          const endRotate = parseFloat(hero.dataset.rotateEnd) || 0;
          const rotate = startRotate - (startRotate - endRotate) * scrollProgress;

          // Border radius: starts at 54px, ends at 0
          const startRadius = parseFloat(hero.dataset.radiusStart) || 54;
          const endRadius = parseFloat(hero.dataset.radiusEnd) || 0;
          const radius = startRadius - (startRadius - endRadius) * scrollProgress;

          hero.style.setProperty('--hero-scale', scale);
          hero.style.setProperty('--hero-rotate', `${rotate}deg`);
          hero.style.setProperty('--hero-radius', `${radius}px`);
        });

        // CTA animations
        ctaSections.forEach(cta => {
          const rect = cta.getBoundingClientRect();
          const ctaTop = rect.top;
          
          // Calculate progress based on element position in viewport
          const scrollProgress = Math.max(0, Math.min(1, 
            1 - (ctaTop / windowHeight)
          ));

          // Scale: starts at 0.95, ends at 1
          const startScale = parseFloat(cta.dataset.scaleStart) || 0.95;
          const endScale = parseFloat(cta.dataset.scaleEnd) || 1;
          const scale = startScale + (endScale - startScale) * scrollProgress;

          // Border radius: starts at 54px, ends at 0
          const startRadius = parseFloat(cta.dataset.radiusStart) || 54;
          const endRadius = parseFloat(cta.dataset.radiusEnd) || 0;
          const radius = startRadius - (startRadius - endRadius) * scrollProgress;

          cta.style.setProperty('--sl-cta-scale', scale);
          cta.style.setProperty('--sl-cta-radius', `${radius}px`);
        });
      }

      window.addEventListener('scroll', handleScroll, { passive: true });
      updateScrollAnimations(); // Initial call
    }
  }

  // ============================================
  // Parallax Effect for Counter Cards
  // ============================================
  function initParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    
    if (!parallaxElements.length) return;

    let ticking = false;

    function handleScroll() {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateParallax();
          ticking = false;
        });
        ticking = true;
      }
    }

    function updateParallax() {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      parallaxElements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const elementCenter = rect.top + rect.height / 2;
        const viewportCenter = windowHeight / 2;
        
        // Calculate offset from center
        const offset = (elementCenter - viewportCenter) / windowHeight;
        const speed = parseFloat(el.dataset.parallax) || CONFIG.parallaxFactor;
        const translateY = offset * speed * 50;

        el.style.transform = `translateY(${translateY}px)`;
      });
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateParallax();
  }

  // ============================================
  // Marquee (Badges) Control
  // ============================================
  function initMarquee() {
    const marqueeContainers = document.querySelectorAll('.sl-badges--marquee');
    
    marqueeContainers.forEach(container => {
      const track = container.querySelector('.sl-badges__track');
      
      if (!track) return;

      // Duplicate content for seamless loop
      const content = track.innerHTML;
      track.innerHTML = content + content;

      // Pause on hover
      const pauseOnHover = container.dataset.pauseOnHover !== 'false';
      
      if (pauseOnHover) {
        container.addEventListener('mouseenter', () => {
          container.classList.add('sl-badges--paused');
        });
        
        container.addEventListener('mouseleave', () => {
          container.classList.remove('sl-badges--paused');
        });
      }
    });
  }

  // ============================================
  // Image Lazy Loading Enhancement
  // ============================================
  function initLazyLoading() {
    const lazyImages = document.querySelectorAll('.sl-gallery__item img[loading="lazy"]');
    
    if (!lazyImages.length || !('IntersectionObserver' in window)) return;

    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          
          // Add loaded class for fade-in effect
          img.addEventListener('load', () => {
            img.classList.add('sl-loaded');
          });
          
          imageObserver.unobserve(img);
        }
      });
    }, {
      rootMargin: '100px'
    });

    lazyImages.forEach(img => imageObserver.observe(img));
  }

  // ============================================
  // Accessibility: Reduced Motion Check
  // ============================================
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ============================================
  // Initialize All
  // ============================================
  function init() {
    // Skip animations if user prefers reduced motion
    if (prefersReducedMotion()) {
      // Still initialize counters but without animation
      const counters = document.querySelectorAll('.sl-counter__value[data-target]');
      counters.forEach(counter => {
        const target = counter.dataset.target;
        const suffix = counter.dataset.suffix || '';
        counter.innerHTML = `${target}<span class="sl-counter__suffix">${suffix}</span>`;
        counter.classList.add('sl-animated');
      });
      
      // Make all reveal elements visible
      document.querySelectorAll('.sl-reveal, .sl-stagger').forEach(el => {
        el.classList.add('sl-visible');
      });
      
      return;
    }

    initRevealAnimations();
    initCounterAnimations();
    initScrollAnimations();
    initParallax();
    initMarquee();
    initLazyLoading();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose to global scope for potential external use
  window.ServicesLab = {
    init,
    initRevealAnimations,
    initCounterAnimations,
    initScrollAnimations,
    initParallax,
    initMarquee
  };

})();

